import "./QuestionBlock.css";
import {
	type JSX,
	Show,
	createEffect,
	createSignal,
	createUniqueId,
	onCleanup,
	onMount,
} from "solid-js";
import { Button } from "@ui/Button.jsx";
import { HStack } from "@ui/Stack.jsx";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { TextMono } from "@ui/typography/TextMono.jsx";
import { parseMentionJsonInText } from "~/modules/shared/chat/utils/parseMentionJsonInText";
import { mentionPlaceholderId } from "~/modules/shared/chat/utils/parseSpeakingBlockSegments";
import { hydrateMentionPlaceholders } from "~/modules/shared/chat/utils/richTextDomHydration";
import { videoMentionFromSegment } from "~/modules/shared/chat/utils/videoMentionPlayback";
import { getScrollParent } from "~/modules/shared/utils/scrollParent";
import { formatMarkdown } from "~/modules/shared/utils/text";
import { type ContextAnswer, ContextAnswerPill } from "./ContextAnswerPill.tsx";
import { Mention } from "./Mention.tsx";
import { VideoMentionPill } from "./VideoMentionPill.tsx";

const MAX_VISIBLE_LINES = 5;
// Keep in sync with the max-height transition duration in QuestionBlock.css.
const COLLAPSE_ANIMATION_MS = 300;

function parsePrompt(prompt: string) {
	return parseMentionJsonInText(prompt);
}

function lineHeightPx(el: HTMLElement) {
	const styles = getComputedStyle(el);
	const lineHeight = parseFloat(styles.lineHeight);
	if (!Number.isNaN(lineHeight)) {
		return lineHeight;
	}
	const fontSize = parseFloat(styles.fontSize) || 16;
	return fontSize * 1.2;
}

function collapsedHeightPx(el: HTMLElement) {
	return MAX_VISIBLE_LINES * lineHeightPx(el);
}

const ShowMoreButton = (props: { open: boolean; onToggle: () => void }) => {
	return (
		<Button
			variant="ghost"
			theme="monochrome"
			class="question-block-show-more"
			classList={{ open: props.open }}
			onClick={props.onToggle}
		>
			<HStack spacing="small" align="center">
				<TextMono color="fg2">
					<Show when={props.open} fallback={"Show more"}>
						Show less
					</Show>
				</TextMono>
				<MaterialSymbol
					symbol="keyboard_arrow_down"
					class="expand"
					relativeSize="even-larger"
					color="fg4"
				/>
			</HStack>
		</Button>
	);
};

type QuestionBlockProps = {
	prompt: string;
	appEmbedded: boolean;
	/** The saved answer the question was asked with, shown before the prompt. */
	contextAnswer?: ContextAnswer;
};

export const QuestionBlock = (props: QuestionBlockProps) => {
	const idBase = createUniqueId();
	const disposers: Array<() => void> = [];

	const [expanded, setExpanded] = createSignal(false);
	const [animating, setAnimating] = createSignal(false);
	const [canExpand, setCanExpand] = createSignal(false);

	// Only clamp (and therefore show the ellipsis) while collapsed and at rest.
	// During the animation the height is driven by an inline max-height instead.
	const clamped = () => !expanded() && !animating();

	let contentRef: HTMLDivElement | undefined;
	let scrollFrame: number | undefined;

	// Render the prompt through the shared Markdown formatter so newlines,
	// lists, emphasis, etc. display properly. Mentions and video mentions are
	// swapped out for placeholder tokens before formatting and then hydrated
	// back into inline Solid components afterwards.
	const renderPrompt = () => {
		const el = contentRef;
		if (!el) {
			return;
		}
		for (const dispose of disposers.splice(0)) {
			dispose();
		}

		const segments = parsePrompt(props.prompt);
		const appEmbedded = props.appEmbedded;
		const contextAnswer = props.contextAnswer;
		const placeholders = new Map<string, () => JSX.Element>();
		let markdown = "";
		let counter = 0;

		if (contextAnswer) {
			const id = mentionPlaceholderId(idBase, counter++);
			markdown += `${id} `;
			placeholders.set(id, () => <ContextAnswerPill answer={contextAnswer} />);
		}

		for (const seg of segments) {
			if (seg.type === "text") {
				markdown += seg.value;
				continue;
			}

			const id = mentionPlaceholderId(idBase, counter++);
			markdown += id;

			if (seg.type === "mention") {
				const mention = seg.value;
				placeholders.set(id, () => (
					<Mention mention={mention} appEmbedded={appEmbedded} />
				));
			} else if (seg.type === "video_mention") {
				const mention = videoMentionFromSegment(seg);
				placeholders.set(id, () => (
					<VideoMentionPill mention={mention} tone="brand" />
				));
			}
		}

		let html = formatMarkdown(markdown);
		for (const id of placeholders.keys()) {
			html = html.replace(id, `<span data-mention-placeholder="${id}"></span>`);
		}

		el.innerHTML = html;
		hydrateMentionPlaceholders(el, placeholders, disposers);
		measure();
	};

	const prefersReducedMotion = () =>
		typeof window !== "undefined" &&
		window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

	// Collapsing pulls the bottom of the prompt (its last visible line and the
	// "Show less" button beneath it) upwards. If that would carry the last line
	// above the top of the scroll viewport, nudge the scroll position just enough
	// to keep it visible (without revealing any of the lines we're hiding).
	const keepPromptEndInView = (durationMs: number) => {
		const content = contentRef;
		if (!content) {
			return;
		}
		const scrollParent = getScrollParent(content);
		const viewportTop = scrollParent
			? scrollParent.getBoundingClientRect().top
			: 0;
		const lineHeight = lineHeightPx(content);

		const scrollUpBy = (amount: number) => {
			if (scrollParent) {
				scrollParent.scrollTop -= amount;
			} else {
				window.scrollBy(0, -amount);
			}
		};

		if (scrollFrame !== undefined) {
			cancelAnimationFrame(scrollFrame);
		}
		const start = performance.now();
		const step = () => {
			// Top of the last visible line = bottom of the (clipped) content box
			// minus one line's height.
			const lastLineTop = content.getBoundingClientRect().bottom - lineHeight;
			const overshoot = viewportTop - lastLineTop;
			if (overshoot > 0.5) {
				scrollUpBy(overshoot);
			}
			scrollFrame =
				performance.now() - start < durationMs
					? requestAnimationFrame(step)
					: undefined;
		};
		scrollFrame = requestAnimationFrame(step);
	};

	const measure = () => {
		const el = contentRef;
		if (!el || animating()) {
			return;
		}
		setCanExpand(el.scrollHeight > collapsedHeightPx(el) + 1);
	};

	const expand = (el: HTMLDivElement) => {
		const startHeight = el.clientHeight;
		el.style.overflow = "hidden";
		el.style.maxHeight = `${startHeight}px`;
		setAnimating(true);
		void el.offsetHeight;
		setExpanded(true);
		el.style.maxHeight = `${el.scrollHeight}px`;
		const onEnd = (event: TransitionEvent) => {
			if (event.propertyName !== "max-height") {
				return;
			}
			el.style.maxHeight = "";
			el.style.overflow = "";
			el.removeEventListener("transitionend", onEnd);
			setAnimating(false);
		};
		el.addEventListener("transitionend", onEnd);
	};

	const collapse = (el: HTMLDivElement) => {
		const targetHeight = collapsedHeightPx(el);
		el.style.overflow = "hidden";
		el.style.maxHeight = `${el.scrollHeight}px`;
		setAnimating(true);
		setExpanded(false);
		void el.offsetHeight;
		el.style.maxHeight = `${targetHeight}px`;
		keepPromptEndInView(COLLAPSE_ANIMATION_MS);
		const onEnd = (event: TransitionEvent) => {
			if (event.propertyName !== "max-height") {
				return;
			}
			el.removeEventListener("transitionend", onEnd);
			// Re-enable the clamp before dropping the inline height so the resting
			// state (ellipsis at line 5) exactly matches the animated target.
			setAnimating(false);
			el.style.maxHeight = "";
			el.style.overflow = "";
		};
		el.addEventListener("transitionend", onEnd);
	};

	const toggle = () => {
		const el = contentRef;
		if (!el || prefersReducedMotion()) {
			const wasExpanded = expanded();
			setExpanded((value) => !value);
			if (wasExpanded) {
				// No animation, so correct the scroll position on the next frame
				// once the collapsed layout has settled.
				keepPromptEndInView(0);
			}
			return;
		}
		if (expanded()) {
			collapse(el);
		} else {
			expand(el);
		}
	};

	createEffect(renderPrompt);

	onMount(() => {
		const el = contentRef;
		if (!el) {
			return;
		}
		measure();
		const resizeObserver = new ResizeObserver(measure);
		resizeObserver.observe(el);
		onCleanup(() => {
			resizeObserver.disconnect();
			if (scrollFrame !== undefined) {
				cancelAnimationFrame(scrollFrame);
			}
		});
	});

	onCleanup(() => {
		for (const dispose of disposers.splice(0)) {
			dispose();
		}
	});

	return (
		<div class="question-block-question">
			<div class="prompt" classList={{ "has-show-more": canExpand() }}>
				<div
					ref={contentRef}
					class="prompt-content"
					classList={{ "is-clamped": clamped() }}
				/>
				<Show when={canExpand()}>
					<ShowMoreButton open={expanded()} onToggle={toggle} />
				</Show>
			</div>
		</div>
	);
};
